import { useQueryClient } from "@tanstack/react-query";
import { ArrowRight, Building2, Globe2 } from "lucide-react";
import { useState } from "react";
import { Navigate, useNavigate } from "react-router-dom";
import { api, errorMessage, unwrap } from "../api/client";
import { useOrg } from "../api/org";
import { useAuth } from "../auth/auth";
import { Alert, Button, Field, Select, TextInput } from "../components/ui";
import { normalizeOrigin, originProblem } from "../lib/snippets";

export const COUNTRIES = [
  { value: "US", label: "United States", currency: "USD" },
  { value: "CA", label: "Canada", currency: "CAD" },
  { value: "GB", label: "United Kingdom", currency: "GBP" },
  { value: "AU", label: "Australia", currency: "AUD" },
  { value: "NZ", label: "New Zealand", currency: "NZD" },
  { value: "IE", label: "Ireland", currency: "EUR" },
  { value: "DE", label: "Germany", currency: "EUR" },
  { value: "NL", label: "Netherlands", currency: "EUR" },
  { value: "ES", label: "Spain", currency: "EUR" },
  { value: "IT", label: "Italy", currency: "EUR" },
  { value: "FR", label: "France", currency: "EUR" },
  { value: "MX", label: "Mexico", currency: "MXN" },
] as const;

export function Onboarding() {
  const { orgId, me } = useOrg();
  const { signOut } = useAuth();
  const qc = useQueryClient();
  const navigate = useNavigate();
  const [name, setName] = useState("");
  const [country, setCountry] = useState<string>("US");
  const [website, setWebsite] = useState("");
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);

  if (orgId && !busy) return <Navigate to="/get-started" replace />;

  const origin = normalizeOrigin(website);
  const websiteProblem = website.trim() ? originProblem(origin) : null;

  async function submit() {
    setBusy(true);
    setError(null);
    try {
      const currency = COUNTRIES.find((c) => c.value === country)?.currency;
      const org = await unwrap(
        api.POST("/v1/orgs", { body: { name: name.trim(), country, default_jurisdiction: country, currency } }),
      );
      const path = { org_id: org.id };
      // The live key for the website (its origin when given; more can be added on Get started).
      await unwrap(
        api.POST("/v1/orgs/{org_id}/embed-keys", {
          params: { path },
          body: { label: "Website", mode: "live", allowed_origins: origin && !websiteProblem ? [origin] : [] },
        }),
      );
      // The calculator shows the company's name and contact from the start.
      try {
        const settings = await unwrap(api.GET("/v1/orgs/{org_id}/settings", { params: { path } }));
        const brand = settings.documents.brand;
        await unwrap(
          api.PUT("/v1/orgs/{org_id}/settings/{kind}", {
            params: { path: { ...path, kind: "brand" } },
            body: {
              document: {
                ...brand,
                company_name: name.trim(),
                contact: { ...brand.contact, email: brand.contact.email || me.user.email, url: origin || brand.contact.url },
              },
            },
          }),
        );
      } catch {
        /* branding can be set later */
      }
      localStorage.setItem("solaros.org", org.id);
      await qc.invalidateQueries({ queryKey: ["me"] });
      navigate("/get-started", { replace: true });
    } catch (e) {
      setError(errorMessage(e));
      setBusy(false);
    }
  }

  return (
    <div className="auth-page">
      <div className="auth-art">
        <div className="brand" style={{ padding: 0 }}>
          <img src="/solaros-mark.png" alt="" />
          <div>
            <b>SolarOS</b>
          </div>
        </div>
        <div>
          <h2>
            Two minutes to your first <span className="gradient-text">website quote.</span>
          </h2>
          <p>Create your company, paste one snippet on your site, and visitors get an AI-designed, priced solar proposal — branded as you.</p>
        </div>
        <img className="roof" src="/roof-design.webp" alt="" />
      </div>
      <div className="auth-form">
        <form
          className="auth-card"
          onSubmit={(e) => {
            e.preventDefault();
            void submit();
          }}
        >
          <div>
            <span className="badge info plain">Step 1 of 2</span>
            <h1 style={{ marginTop: 12 }}>Create your company</h1>
            <p className="muted" style={{ marginTop: 6 }}>
              Signed in as <b>{me.user.email}</b>. Your first design is on us.
            </p>
          </div>
          {error && <Alert tone="danger">{error}</Alert>}
          <Field label={<><Building2 size={14} /> Company name</>}>
            <TextInput required maxLength={200} value={name} onChange={(e) => setName(e.target.value)} placeholder="Sunrise Solar Co." autoFocus />
          </Field>
          <Field label="Country">
            <Select value={country} onChange={setCountry} options={COUNTRIES.map((c) => ({ value: c.value, label: c.label }))} />
          </Field>
          <Field
            label={<><Globe2 size={14} /> Your website</>}
            hint="Where the quote calculator will live. You can add more later."
            error={websiteProblem}
          >
            <TextInput value={website} onChange={(e) => setWebsite(e.target.value)} placeholder="www.sunrisesolar.com" inputMode="url" />
          </Field>
          <Button type="submit" variant="primary" size="lg" block loading={busy} disabled={!name.trim() || !!websiteProblem} icon={<ArrowRight />}>
            Create company
          </Button>
          <p className="small faint">
            Wrong account?{" "}
            <button type="button" className="link-btn" onClick={() => signOut()}>
              Sign out
            </button>
          </p>
        </form>
      </div>
    </div>
  );
}
